import { useState } from 'react'
import type { JSX } from 'react'

import { graphql, maskFragments } from '~publish/graphql'
import type { FragmentOf, ResultOf } from '~publish/graphql'
import type { CommentsPostCard_PostData } from '../../fragments/CommentsPostCard_Post'

import { CommentMedia } from '../CommentMedia'
import { CommentsPostCard_Post } from '../../fragments/CommentsPostCard_Post'
import { CommentUnsupportedContent } from '../CommentUnsupportedContent'
import { CommentHeader, CommentHeader_Channel } from './CommentHeader'
import { CommentText } from './CommentText'
import {
  CommentReactions,
  CommentReactions_Channel,
} from '../../components/CommentReactions'
import {
  EngagementAvatar,
  EngagementAvatar_Channel,
} from '../../components/EngagementAvatar'
import { EngagementItem } from '../../components/EngagementItem'
import { CommentContextProvider } from '../context/CommentContext'
import type { CommentData, LastReply } from '../fragments/commentData'
import type { OptimisticReply } from '../../hooks/comments/useReplyToComment'

const COMMENT_CHANNEL_SERVICES = [
  'startPage',
  'bluesky',
  'facebook',
  'googlebusiness',
  'instagram',
  'linkedin',
  'mastodon',
  'pinterest',
  'threads',
  'tiktok',
  'twitter',
  'youtube',
] satisfies CommentsPostCard_PostData['channelService'][]

function isCommentChannelService(
  service: string,
): service is CommentsPostCard_PostData['channelService'] {
  return COMMENT_CHANNEL_SERVICES.some(
    (supportedService) => supportedService === service,
  )
}

export const CommentLastReplies_Channel = graphql(
  /* GraphQL */ `
    fragment CommentLastReplies_Channel on Channel @_unmask {
      id
      avatar
      service
      serviceId
      type
      name
      displayName
      ...CommentHeader_Channel
      ...CommentReactions_Channel
      ...EngagementAvatar_Channel
    }
  `,
  [CommentHeader_Channel, CommentReactions_Channel, EngagementAvatar_Channel],
)

type CommentLastReplyProps = {
  channel: FragmentOf<typeof CommentLastReplies_Channel>
  reply?: CommentData | LastReply | OptimisticReply | null | undefined
  showConnector?: boolean
}

export const CommentLastReply = ({
  channel,
  reply,
  showConnector = false,
}: CommentLastReplyProps): JSX.Element | null => {
  const [isHovered, setIsHovered] = useState(false)

  if (!reply || !isCommentChannelService(channel.service)) {
    return null
  }

  // Generate stable ID for semantic grouping
  const replyId = `reply-${channel.id}-${reply.author.username}`
    .replace(/\s+/g, '-')
    .toLowerCase()

  const replyPost = {
    __typename: 'Post',
    id: `reply-post-${reply.id}`,
    status: 'sent',
    tags: [],
    metadata: null,
    assets: [],
    dueAt: null,
    schedulingType: null,
    isCustomScheduled: false,
    isPinned: false,
    notificationStatus: null,
    text: reply.text,
    channelId: channel.id,
    channelService: channel.service,
    externalLink: null,
    channel: {
      id: channel.id,
      service: channel.service,
      serviceId: channel.serviceId ?? '',
      type: channel.type ?? 'profile',
      name: channel.name ?? '',
      displayName: channel.displayName ?? '',
    },
  } satisfies ResultOf<typeof CommentsPostCard_Post>

  const replyAsComment: CommentData = {
    ...reply,
    __typename: 'CommentFromThirdParty',
    post: {
      ...replyPost,
      ...maskFragments([CommentsPostCard_Post], replyPost),
    },
    postPublishedAt: reply.publishedAt ?? '',
    lastReplies: [],
    isDeleted: false,
    labels: [],
    suggestions: [],
    sentimentAnalysis: null,
  }

  return (
    <CommentContextProvider comment={replyAsComment}>
      <EngagementItem
        as="article"
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- This related reply is exposed as a labelled complementary landmark within the comment.
        role="complementary"
        aria-label="Author's reply"
        aria-labelledby={`${replyId}-header`}
        data-testid="comment-item"
        data-reply-id={reply.id}
        variant="lastReply"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        avatar={
          <EngagementAvatar
            hasParent={true}
            avatar={reply.isOwnReply ? channel.avatar : reply.author.avatar}
            username={reply.author.username ?? 'username'}
            showConnector={showConnector}
          />
        }
      >
        <CommentHeader
          id={`${replyId}-header`}
          isHovered={isHovered}
          isOwnReply={reply.isOwnReply}
          channel={channel}
        />
        <CommentText />
        <CommentMedia />
        {replyAsComment.contentType === 'unsupported' && (
          <CommentUnsupportedContent />
        )}
        <CommentReactions channel={channel} />
      </EngagementItem>
    </CommentContextProvider>
  )
}

type CommentLastRepliesProps = {
  channel: FragmentOf<typeof CommentLastReplies_Channel>
  lastReplies?:
    | Array<CommentData | LastReply | OptimisticReply | null>
    | null
    | undefined
  isProcessing: boolean
  isFormVisible: boolean
}

/**
 * Renders all replies (includes both server replies and local sent replies).
 * Uses merged array from parent component.
 */
export const CommentLastReplies = ({
  channel,
  lastReplies,
  isProcessing,
  isFormVisible,
}: CommentLastRepliesProps): JSX.Element | null => {
  return (
    <>
      {/* Renders all replies (parent component merges sentReplies + server lastReplies) */}
      {lastReplies?.map((serverReply, index) => {
        if (!serverReply) return null

        const isLast = index === (lastReplies?.length ?? 0) - 1
        // Show connector if not last, or if there's content below (processing or form)
        const shouldShowConnector = !isLast || isProcessing || isFormVisible

        return (
          <CommentLastReply
            key={serverReply.id}
            channel={channel}
            reply={serverReply}
            showConnector={shouldShowConnector}
          />
        )
      })}
    </>
  )
}
